import { useState } from 'react'
import type { Project, Task, User } from '@/types'

interface UseDocumentAssociationsOptions {
  initialProjects?: Project[]
  initialTasks?: Task[]
  initialUsers?: User[]
}

export function useDocumentAssociations(options: UseDocumentAssociationsOptions = {}) {
  const [selectedProjects, setSelectedProjects] = useState<Project[]>(options.initialProjects || [])
  const [selectedTasks, setSelectedTasks] = useState<Task[]>(options.initialTasks || [])
  const [selectedUsers, setSelectedUsers] = useState<User[]>(options.initialUsers || [])

  const handleAddProject = (projectId: string, availableProjects: Project[]) => {
    const project = availableProjects.find((p) => p.id === projectId)
    if (project && !selectedProjects.some((p) => p.id === projectId)) {
      setSelectedProjects([...selectedProjects, project])
    }
  }

  const handleRemoveProject = (projectId: string) => {
    setSelectedProjects(selectedProjects.filter((p) => p.id !== projectId))
  }

  const handleAddTask = (taskId: string, availableTasks: Task[]) => {
    const task = availableTasks.find((t) => t.id === taskId)
    if (task && !selectedTasks.some((t) => t.id === taskId)) {
      setSelectedTasks([...selectedTasks, task])
    }
  }

  const handleRemoveTask = (taskId: string) => {
    setSelectedTasks(selectedTasks.filter((t) => t.id !== taskId))
  }

  const handleAddUser = (userId: string, availableUsers: User[]) => {
    const selectedUser = availableUsers.find((u) => u.id === userId)
    if (selectedUser && !selectedUsers.some((u) => u.id === userId)) {
      setSelectedUsers([...selectedUsers, selectedUser])
    }
  }

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers(selectedUsers.filter((u) => u.id !== userId))
  }

  const resetAssociations = () => {
    setSelectedProjects([])
    setSelectedTasks([])
    setSelectedUsers([])
  }

  const setAssociations = (projects: Project[], tasks: Task[], users: User[]) => {
    setSelectedProjects(projects)
    setSelectedTasks(tasks)
    setSelectedUsers(users)
  }

  const filterAssociationsByAvailable = (
    availableProjects: Project[],
    availableTasks: Task[],
    availableUsers: User[]
  ) => {
    // Filter selected projects to only include those available in the new workspace
    setSelectedProjects((current) =>
      current.filter((selected) =>
        availableProjects.some((available) => available.id === selected.id)
      )
    )
    // Filter selected tasks to only include those available in the new workspace
    setSelectedTasks((current) =>
      current.filter((selected) => availableTasks.some((available) => available.id === selected.id))
    )
    // Filter selected users to only include those available in the new workspace
    setSelectedUsers((current) =>
      current.filter((selected) => availableUsers.some((available) => available.id === selected.id))
    )
  }

  const getCounts = () => ({
    projects: selectedProjects.length,
    tasks: selectedTasks.length,
    users: selectedUsers.length,
  })

  return {
    selectedProjects,
    selectedTasks,
    selectedUsers,
    handleAddProject,
    handleRemoveProject,
    handleAddTask,
    handleRemoveTask,
    handleAddUser,
    handleRemoveUser,
    resetAssociations,
    setAssociations,
    filterAssociationsByAvailable,
    getCounts,
  }
}
